import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import dayjs from 'dayjs';
import sanitizeHtml from 'sanitize-html';
import { cn, ResponsiveUtils, RoutePaths } from '@shared/utils';
import type { GetMessagesResponse } from '@shared/api';
import { ViewMessage } from './viewMessage';
import { PrintableContent, PrintStyle, PrintType, SearchHighlight } from '@shared/widgets';
import { MessagePrintableContent } from './messagePrintableContent';

interface MessageItemProps {
  className?: string;
  isShown: boolean;
  message: GetMessagesResponse;
  redirect: 'inbox' | 'sent';
  isFirst?: boolean;
  isLast?: boolean;
  searchQuery?: string;
  scrollable?: boolean;
  onShowMessage: (message: GetMessagesResponse) => void;
}

export const MessageItem = (props: MessageItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [viewPrintVersion, setViewPrintVersion] = useState<boolean>(false);
  const [height, setHeight] = useState<number>(64);

  useEffect(() => {
    const element = document.getElementById(props.message.Id);
    if (element) setHeight(element.clientHeight);
  }, [props.message.Id]);

  useEffect(() => {
    if (props.isShown) props.onShowMessage?.(props.message);
  }, [props.isShown, props.onShowMessage, props.message]);

  const hideLetter = (e: React.MouseEvent<SVGSVGElement | HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    navigate(RoutePaths.messages.onNavigate({ dir: props.redirect }, { queryString: location.search }));
  };

  const itemStyle: Record<string, number> = {};
  if (props.isShown && !ResponsiveUtils.isMobile.any()) {
    // 42px is top in styles
    const newHeight = height - 42;
    itemStyle.height = newHeight > 240 ? 240 : newHeight;
  }

  return (
    <div
      className={cn(
        'message-item relative flex h-[68px] cursor-pointer items-center justify-center pl-[130px] transition-[height_300ms_ease]',
        'max-md:p-0',
        props.isShown && ['z-[800]', 'max-md:!z-[1500]'],
        props.className,
      )}
      style={itemStyle}
    >
      <div
        className={cn(
          'absolute top-0 left-0 flex h-full min-w-[130px] items-center justify-center bg-[#1f1f1f] text-sm text-[#929292] shadow-[inset_0_2px_6px_#0000001a]',
          props.isFirst && 'rounded-tl-sm',
          props.isLast && 'rounded-bl-sm',
          props.message.UnreadFlag && 'text-[#929292]',
          props.isShown && 'items-start pt-6',
        )}
      >
        {dayjs(props.message.Date).format('DD.MM.YYYY')}
      </div>
      <Link
        className={cn(
          'message-item__block relative flex h-full w-full items-center justify-start bg-[#eee] px-5 text-[#000] no-underline shadow-[inset_0_2px_6px_#00000026] max-md:flex-col max-md:items-start max-md:bg-[#eee] max-md:p-0 max-md:pt-2.5 max-md:pl-4',
          props.isFirst && ['rounded-tr-sm', 'max-md:rounded-t-sm'],
          props.isLast && ['rounded-br-sm', 'max-md:rounded-b-sm'],
          props.message.UnreadFlag && [
            '!bg-[#fbfbfb]',
            'before:absolute before:top-0 before:left-[-3px] before:h-full before:w-[3px] before:bg-[#97815e]',
            'max-md:!bg-[#fff] max-md:before:left-0 max-md:before:w-[2px]',
            props.isFirst && 'max-md:before:rounded-tl-lg',
            props.isLast && 'max-md:before:rounded-bl-sm',
          ],
        )}
        to={RoutePaths.messages.onNavigate({ dir: props.redirect, id: props.message.Id }, { queryString: location.search })}
      >
        <div
          className={cn(
            'mr-6 w-[30%] overflow-hidden font-medium text-ellipsis whitespace-nowrap uppercase',
            'max-md:m-0 max-md:mb-1 max-md:w-[70%]',
            props.message.UnreadFlag && 'font-bold',
          )}
        >
          <SearchHighlight searchText={props.searchQuery}>{sanitizeHtml(props.message.Subject)}</SearchHighlight>
        </div>
        <div className='w-full overflow-hidden'>
          <div className='overflow-hidden text-xs font-medium text-ellipsis whitespace-nowrap uppercase max-md:w-[90wv]'>
            <SearchHighlight searchText={props.searchQuery}>
              {sanitizeHtml(props.message.Body, { allowedTags: ['b', 'i', 'em', 'strong'] })}
            </SearchHighlight>
          </div>
        </div>
        <div className='absolute top-[10px] right-[20px] hidden text-xs font-semibold text-[#626262] max-md:block'>
          {dayjs(props.message.Date).format('DD.MM.YYYY')}
        </div>
        <ViewMessage
          className={cn(props.isFirst && '!top-0')}
          isShown={props.isShown}
          message={props.message}
          searchQuery={props.searchQuery}
          hideLetter={hideLetter}
          handlePrint={() => setViewPrintVersion(true)}
          scrollable={props.scrollable}
        />
        <div className={cn('fixed top-0 right-0 bottom-0 left-0 z-[30] hidden bg-[#0000004d]', props.isShown && 'block')} onClick={hideLetter} />
      </Link>
      {props.isShown && (
        <PrintableContent
          title={props.message.Subject}
          print={viewPrintVersion}
          onAfterPrint={() => setViewPrintVersion(false)}
          printStyle={PrintStyle.getStyle(PrintType.Message)}
        >
          <MessagePrintableContent message={props.message} />
        </PrintableContent>
      )}
    </div>
  );
};
